/*
 * Survivor equipment is modelled as an atomic package:
 * one Item + two different compatible Add-ons + one intended scenario.
 *
 * Strength is deliberately scenario-specific. Rarity is never used as a
 * shortcut for quality: the documented mechanics of the Item and Add-ons
 * decide where a package is strong and where it is merely situational.
 */

export const SURVIVOR_EQUIPMENT_SCENARIOS = [
  {
    id: 'team_aura_tracking',
    automatic: false,
    category: 1,
    target: 'team',
    phase: 'macro',
    plans: ['aura_information', 'linked_teamwork'],
    signals: ['information'],
    keywords: ['aura', 'teamwork', 'survivor', 'partner'],
    labels: { en: 'Team aura tracking', fr: 'Suivi d’aura d’équipe' },
    descriptions: {
      en: 'Find teammates repeatedly to coordinate repairs, heals and rotations.',
      fr: 'Retrouver régulièrement les alliés pour coordonner réparations, soins et rotations.'
    },
    conditions: {
      en: 'Best when the team is spread out or the build rewards proximity.',
      fr: 'Meilleur quand l’équipe est dispersée ou que le build récompense la proximité.'
    }
  },
  {
    id: 'killer_aura_tracking',
    automatic: false,
    category: 1,
    target: 'killer',
    phase: 'chase',
    plans: ['aura_information', 'chase_escape'],
    signals: ['information', 'chase'],
    keywords: ['killer', 'aura', 'chase'],
    labels: { en: 'Killer aura tracking', fr: 'Tracking d’aura du tueur' },
    descriptions: {
      en: 'Trade Key resources and information exposure for direct Killer tracking.',
      fr: 'Échanger des ressources de clé et une exposition d’aura contre un tracking direct du tueur.'
    },
    conditions: {
      en: 'Blood Amber reveals the user to the Killer too; use it in short, deliberate reads.',
      fr: 'Ambre de sang révèle aussi votre aura au tueur : utilisez-le par lectures courtes et intentionnelles.'
    }
  },
  {
    id: 'chest_endgame_utility',
    automatic: false,
    category: 1,
    target: 'objective',
    phase: 'endgame',
    plans: ['item_economy', 'endgame_survival'],
    signals: ['loot', 'endgame', 'information'],
    keywords: ['chest', 'loot', 'rummage', 'hatch', 'endgame'],
    labels: { en: 'Chest and endgame utility', fr: 'Utilité coffres et endgame' },
    descriptions: {
      en: 'Accelerate the Key interaction loop and preserve enough uses for late-game utility.',
      fr: 'Accélérer la boucle d’interaction de la clé et conserver assez d’utilisations pour la fin de partie.'
    },
    conditions: {
      en: 'Dull and Skeleton Keys unlock the full chest and Hatch utility.',
      fr: 'Les clés ternie et passe-partout débloquent toute l’utilité coffres et trappe.'
    }
  },
  {
    id: 'obsession_coordination',
    automatic: false,
    category: 1,
    target: 'team',
    phase: 'macro',
    plans: ['linked_teamwork', 'aura_information'],
    signals: ['information', 'obsession'],
    keywords: ['obsession', 'partner', 'teamwork', 'aura'],
    labels: { en: 'Obsession coordination', fr: 'Coordination avec l’obsession' },
    descriptions: {
      en: 'Maintain permanent two-way information with the Obsession and use the Key for the rest of the team.',
      fr: 'Maintenir une information permanente à double sens avec l’obsession et garder la clé pour le reste de l’équipe.'
    },
    conditions: {
      en: 'Value depends on the Obsession being able to convert the shared aura information.',
      fr: 'La valeur dépend de la capacité de l’obsession à exploiter l’information d’aura partagée.'
    }
  },
  {
    id: 'flashlight_save',
    category: 3,
    target: 'team',
    phase: 'rescue',
    plans: ['rescue_teamplay', 'linked_teamwork'],
    signals: ['hook', 'information', 'item'],
    keywords: ['save', 'rescue', 'pickup', 'flashlight', 'backgroundplayer'],
    labels: { en: 'Flashlight rescue', fr: 'Flash save' },
    descriptions: {
      en: 'Preserve side rescue angles first, then secure enough battery and aim stability to convert Killer pickups reliably.',
      fr: 'Préserver d’abord les angles de sauvetage latéraux, puis garantir assez d’autonomie et de stabilité pour convertir les ramassages du tueur.'
    },
    conditions: {
      en: 'Wide Lens is the enabling mechanic; reach and Blind duration cannot compensate for a narrow cone against wall-facing or turning Killers.',
      fr: 'La Lentille grand-angle est la mécanique activante : portée et durée ne compensent pas un cône étroit face à un tueur collé au mur ou qui se retourne.'
    }
  },
  {
    id: 'chase_blind',
    category: 3,
    target: 'killer',
    phase: 'chase',
    plans: ['chase_escape', 'stealth_evasion'],
    signals: ['chase', 'stealth'],
    keywords: ['blind', 'chase', 'pallet', 'window', 'escape'],
    labels: { en: 'Chase blind', fr: 'Aveuglement en chase' },
    descriptions: {
      en: 'Make close-range blinds easier to land after pallets, vaults and animation locks.',
      fr: 'Faciliter les aveuglements à courte portée après palettes, fenêtres et animations verrouillées.'
    },
    conditions: {
      en: 'Beam width and accuracy add limited comfort; positioning and timing remain decisive, and Wide Lens sacrifices range.',
      fr: 'Largeur et précision ajoutent un confort limité ; placement et timing restent décisifs, et Lentille grand-angle sacrifie de la portée.'
    }
  },
  {
    id: 'blind_duration_pressure',
    category: 3,
    target: 'killer',
    phase: 'chase',
    plans: ['chase_escape', 'stealth_evasion'],
    signals: ['chase', 'stealth'],
    keywords: ['blind', 'disappear', 'duration', 'stealth'],
    labels: { en: 'Blind-duration pressure', fr: 'Pression par durée d’aveuglement' },
    descriptions: {
      en: 'Extend successful blinds to create a larger repositioning or disappearance window.',
      fr: 'Prolonger les aveuglements réussis pour agrandir la fenêtre de repositionnement ou de disparition.'
    },
    conditions: {
      en: 'Higher brightness is mostly visual; the useful payoff here is Blindness duration.',
      fr: 'La luminosité est surtout visuelle ; le gain utile ici est la durée d’aveuglement.'
    }
  },
  {
    id: 'flashlight_endurance',
    category: 3,
    target: 'resource',
    phase: 'macro',
    plans: ['item_economy', 'rescue_teamplay'],
    signals: ['item', 'loot'],
    keywords: ['item', 'charge', 'efficiency', 'streetwise', 'builtlast'],
    labels: { en: 'Flashlight endurance', fr: 'Autonomie de lampe' },
    descriptions: {
      en: 'Maximise usable beam time for several rescue or chase attempts.',
      fr: 'Maximiser le temps de faisceau utilisable pour plusieurs tentatives de sauvetage ou de chase.'
    },
    conditions: {
      en: 'Strong over a full trial, but it does not improve the difficulty of any single attempt.',
      fr: 'Fort sur toute la partie, mais ne facilite pas directement une tentative isolée.'
    }
  },
  {
    id: 'route_scouting',
    category: 5,
    target: 'self',
    phase: 'chase',
    plans: ['chase_escape', 'aura_information'],
    signals: ['information', 'chase'],
    keywords: ['pallet', 'window', 'vault', 'route', 'aura'],
    labels: { en: 'Route scouting', fr: 'Lecture des routes' },
    descriptions: {
      en: 'Read nearby pallets and windows before committing to a chase route.',
      fr: 'Lire palettes et fenêtres proches avant de s’engager sur une route de chase.'
    },
    conditions: {
      en: 'Most valuable before or between chases, not while already cornered.',
      fr: 'Surtout utile avant ou entre les chases, pas une fois déjà enfermé dans une zone.'
    }
  },
  {
    id: 'team_objective_mapping',
    category: 5,
    target: 'team',
    phase: 'macro',
    plans: ['generator_pressure', 'linked_teamwork', 'aura_information'],
    signals: ['generator', 'information'],
    keywords: ['generator', 'objective', 'beam', 'teamwork', 'aura'],
    labels: { en: 'Team objective mapping', fr: 'Cartographie d’objectifs d’équipe' },
    descriptions: {
      en: 'Create longer, wider information anchors around generators for the whole team.',
      fr: 'Créer des balises d’information plus longues et plus larges autour des générateurs pour toute l’équipe.'
    },
    conditions: {
      en: 'Requires teammates to notice and act on the Beam of Light.',
      fr: 'Demande que les alliés voient la colonne de lumière et exploitent l’information.'
    }
  },
  {
    id: 'totem_hunting',
    category: 5,
    target: 'objective',
    phase: 'macro',
    plans: ['totem_control', 'aura_information'],
    signals: ['totem', 'information'],
    keywords: ['totem', 'hex', 'boon', 'cleanse', 'bless'],
    labels: { en: 'Totem hunting', fr: 'Chasse aux totems' },
    descriptions: {
      en: 'Unlock Totem tracking, then add enough range or duration to search efficiently.',
      fr: 'Débloquer le tracking des totems, puis ajouter assez de portée ou de durée pour chercher efficacement.'
    },
    conditions: {
      en: 'Sharpened Flint is mandatory for the Totem-specific ceiling.',
      fr: 'Silex aiguisé est obligatoire pour atteindre le plafond spécifique aux totems.'
    }
  },
  {
    id: 'map_killer_scouting',
    category: 5,
    target: 'killer',
    phase: 'macro',
    plans: ['aura_information', 'stealth_evasion', 'linked_teamwork'],
    signals: ['information', 'stealth', 'chase'],
    keywords: ['killer', 'aura', 'beam', 'teamwork'],
    labels: { en: 'Killer scouting beacon', fr: 'Balise de repérage du tueur' },
    descriptions: {
      en: 'Turn the team Beam into a local Killer detector and compensate for its resource penalties.',
      fr: 'Transformer la balise d’équipe en détecteur local du tueur et compenser ses pénalités de ressources.'
    },
    conditions: {
      en: 'The Killer must pass close to the Beam; Crimson Stamp reduces charges and Beam life.',
      fr: 'Le tueur doit passer près de la balise ; Tampon cramoisi réduit les charges et sa durée de vie.'
    }
  },
  {
    id: 'double_self_heal',
    category: 6,
    target: 'self',
    phase: 'reset',
    plans: ['healing_support', 'protection_survival', 'item_economy'],
    signals: ['healing', 'item'],
    keywords: ['selfheal', 'injured', 'charge', 'efficiency', 'streetwise'],
    labels: { en: 'Two self-heal reserve', fr: 'Réserve de deux auto-soins' },
    descriptions: {
      en: 'Cross the charge threshold for two complete personal heals instead of over-investing in altruistic speed.',
      fr: 'Franchir le seuil de charges pour deux auto-soins complets au lieu de surinvestir dans la vitesse altruiste.'
    },
    conditions: {
      en: 'Personal Med-Kit healing is slower and less efficient; the package needs at least 48 total charges.',
      fr: 'L’auto-soin au kit est plus lent et moins efficace ; le package exige au moins 48 charges au total.'
    }
  },
  {
    id: 'self_heal_safety',
    category: 6,
    target: 'self',
    phase: 'reset',
    plans: ['healing_support', 'protection_survival'],
    signals: ['healing', 'chase'],
    keywords: ['selfheal', 'injured', 'efficiency', 'safety'],
    labels: { en: 'Emergency self-heal', fr: 'Auto-soin d’urgence' },
    descriptions: {
      en: 'Secure one personal reset with a useful speed or efficiency buffer.',
      fr: 'Sécuriser un reset personnel avec une marge utile de vitesse ou d’efficacité.'
    },
    conditions: {
      en: 'The base Item tier does not improve personal healing speed; Add-ons create the difference.',
      fr: 'Le niveau du kit n’améliore pas la vitesse d’auto-soin ; ce sont les add-ons qui font la différence.'
    }
  },
  {
    id: 'altruistic_heal_burst',
    category: 6,
    target: 'team',
    phase: 'reset',
    plans: ['healing_support', 'rescue_teamplay', 'linked_teamwork'],
    signals: ['healing', 'hook'],
    keywords: ['healother', 'altruism', 'rescue', 'heal', 'unhook'],
    labels: { en: 'Altruistic heal burst', fr: 'Soin altruiste express' },
    descriptions: {
      en: 'Stack the Med-Kit’s altruistic bonus with action-speed Add-ons for the fastest team reset.',
      fr: 'Cumuler le bonus altruiste du kit avec des add-ons de vitesse pour le reset d’équipe le plus rapide.'
    },
    conditions: {
      en: 'Rarer Med-Kits are meaningfully stronger here because their altruistic speed is higher.',
      fr: 'Les kits plus rares sont réellement meilleurs ici grâce à leur vitesse altruiste supérieure.'
    }
  },
  {
    id: 'altruistic_heal_economy',
    category: 6,
    target: 'team',
    phase: 'reset',
    plans: ['healing_support', 'item_economy', 'linked_teamwork'],
    signals: ['healing', 'item'],
    keywords: ['heal', 'altruism', 'charge', 'efficiency', 'teamwork'],
    labels: { en: 'Altruistic heal economy', fr: 'Économie de soins altruistes' },
    descriptions: {
      en: 'Balance charges and healing speed to produce several efficient team resets.',
      fr: 'Équilibrer charges et vitesse pour produire plusieurs resets d’équipe efficaces.'
    },
    conditions: {
      en: 'Less explosive than a pure speed package, but more reliable across a long trial.',
      fr: 'Moins explosif qu’un package tout-vitesse, mais plus fiable sur une partie longue.'
    }
  },
  {
    id: 'healing_skill_checks',
    category: 6,
    target: 'team',
    phase: 'reset',
    plans: ['healing_support', 'generator_pressure'],
    signals: ['healing', 'generator'],
    keywords: ['skillcheck', 'greatskillcheck', 'bonusprogression', 'stakeout'],
    labels: { en: 'Healing skill-check conversion', fr: 'Conversion des skill checks de soin' },
    descriptions: {
      en: 'Create more Healing Skill Checks and increase the reward or consistency of Great results.',
      fr: 'Créer davantage de skill checks de soin et augmenter la récompense ou la régularité des Great.'
    },
    conditions: {
      en: 'Ceiling depends on consistently hitting Great Skill Checks.',
      fr: 'Le plafond dépend de la régularité à réussir les Great Skill Checks.'
    }
  },
  {
    id: 'exhaustion_reset',
    category: 6,
    target: 'self',
    phase: 'chase',
    plans: ['chase_escape', 'healing_support'],
    signals: ['chase', 'healing'],
    keywords: ['exhaustion', 'exhausted', 'sprintburst', 'lithe', 'balancedlanding', 'headon'],
    labels: { en: 'Exhaustion reset', fr: 'Reset d’épuisement' },
    descriptions: {
      en: 'Convert the Med-Kit into an immediate Exhaustion reset for a second chase activation.',
      fr: 'Convertir le kit en reset immédiat d’Épuisement pour une deuxième activation en chase.'
    },
    conditions: {
      en: 'Consumes the Med-Kit and only fits a build with a relevant Exhaustion payoff.',
      fr: 'Consomme le kit et ne convient qu’à un build avec un véritable payoff d’Épuisement.'
    }
  },
  {
    id: 'generator_burst',
    category: 7,
    target: 'objective',
    phase: 'generator',
    plans: ['generator_pressure'],
    signals: ['generator'],
    keywords: ['repair', 'generator', 'progression', 'fasttrack', 'hyperfocus'],
    labels: { en: 'Generator completion pressure', fr: 'Pression de complétion générateur' },
    descriptions: {
      en: 'Maximise the total objective contribution available before the Toolbox is depleted; delivery speed only breaks close ties.',
      fr: 'Maximiser la contribution totale à l’objectif avant l’épuisement de la caisse ; la vitesse de transfert ne départage que les packages proches.'
    },
    conditions: {
      en: 'Capacity and permanent Brand New Part reduction are counted before repair speed.',
      fr: 'La capacité et la réduction permanente de la Pièce flambant neuve sont comptées avant la vitesse de réparation.'
    }
  },
  {
    id: 'generator_capacity',
    category: 7,
    target: 'objective',
    phase: 'generator',
    plans: ['generator_pressure', 'item_economy'],
    signals: ['generator', 'item'],
    keywords: ['repair', 'generator', 'charge', 'efficiency', 'streetwise', 'builtlast'],
    labels: { en: 'Generator capacity', fr: 'Capacité générateur' },
    descriptions: {
      en: 'Maximise total generator progression and the value of item-efficiency perks.',
      fr: 'Maximiser la progression totale de générateur et la valeur des perks d’efficacité d’objet.'
    },
    conditions: {
      en: 'Commodious Toolbox has the best natural capacity; speed determines how safely it is delivered.',
      fr: 'La boîte commodieuse a la meilleure capacité naturelle ; la vitesse détermine la sûreté du transfert.'
    }
  },
  {
    id: 'brand_new_part_burst',
    category: 7,
    target: 'objective',
    phase: 'generator',
    plans: ['generator_pressure'],
    signals: ['generator'],
    keywords: ['brandnewpart', 'progression', 'skillcheck', 'generator'],
    labels: { en: 'Brand New Part objective package', fr: 'Package objectif avec Pièce flambant neuve' },
    descriptions: {
      en: 'Combine the permanent 10-Charge requirement reduction with enough remaining Toolbox capacity to maximise total objective value.',
      fr: 'Combiner la réduction permanente de 10 charges avec assez de capacité restante pour maximiser la valeur totale sur l’objectif.'
    },
    conditions: {
      en: 'The one-use Add-on is prioritised for direct Generator pressure, but not for rechargeable capacity loops.',
      fr: 'L’add-on à usage unique est prioritaire pour la pression générateur directe, mais pas pour les boucles de recharge de capacité.'
    }
  },
  {
    id: 'stealth_repair',
    category: 7,
    target: 'objective',
    phase: 'generator',
    plans: ['generator_pressure', 'stealth_evasion'],
    signals: ['generator', 'stealth'],
    keywords: ['quiet', 'noise', 'stealth', 'repair', 'generator'],
    labels: { en: 'Stealth repair', fr: 'Réparation furtive' },
    descriptions: {
      en: 'Reduce repair noise while keeping enough speed or charges to make the stealth window matter.',
      fr: 'Réduire le bruit de réparation tout en gardant assez de vitesse ou de charges pour rentabiliser la fenêtre furtive.'
    },
    conditions: {
      en: 'Spring Clamp only matters when the Killer is using sound to locate the generator.',
      fr: 'Pince à ressort ne vaut que si le tueur utilise le son pour localiser le générateur.'
    }
  },
  {
    id: 'sabotage_speed',
    automatic: false,
    category: 7,
    target: 'team',
    phase: 'rescue',
    plans: ['sabotage_save', 'rescue_teamplay'],
    signals: ['sabotage', 'hook'],
    keywords: ['sabotage', 'wiggle', 'carrying', 'pickup', 'hookdenial'],
    labels: { en: 'Sabotage speed', fr: 'Vitesse de sabotage' },
    descriptions: {
      en: 'Break the target Hook inside the carry window before the Killer can convert the pickup.',
      fr: 'Casser le crochet ciblé pendant la fenêtre de portage avant que le tueur ne convertisse le ramassage.'
    },
    conditions: {
      en: 'Alex’s Toolbox is the specialised base; Hacksaw and Cutting Wire determine the speed ceiling.',
      fr: 'La boîte d’Alex est la base spécialisée ; Scie à métaux et Fil de coupe fixent le plafond de vitesse.'
    }
  },
  {
    id: 'sabotage_denial',
    category: 7,
    target: 'team',
    phase: 'rescue',
    plans: ['sabotage_save', 'rescue_teamplay'],
    signals: ['sabotage', 'hook', 'save'],
    keywords: ['sabotage', 'hook', 'save', 'carrying', 'hit', 'recovery', 'denial'],
    labels: { en: 'Sabotage denial', fr: 'Déni de crochets' },
    descriptions: {
      en: 'Use Alex\'s 1.5-second action to clear the default successful-hit recovery, then maximise five sabotages and Hook downtime.',
      fr: 'Exploiter l’action de 1,5 s de la boîte d’Alex pour passer sous la récupération standard d’un coup réussi, puis maximiser cinq sabotages et le temps d’indisponibilité.'
    },
    conditions: {
      en: 'Wire Spool raises Alex\'s 18 Charges to 30: exactly five complete sabotages. Grip Wrench adds 20 seconds. Protective Gloves do not hide a face-to-face save or the falling Hook.',
      fr: 'La Bobine de fil fait passer les 18 charges d’Alex à 30 : exactement cinq sabotages complets. La Clé ajustable ajoute 20 s. Les Gants de protection ne cachent ni un sauvetage sous le nez du tueur ni la chute du crochet.'
    }
  },
  {
    id: 'dense_chase_concealment',
    automatic: false,
    category: 8,
    target: 'killer',
    phase: 'chase',
    plans: [],
    signals: ['opacity'],
    keywords: ['opacity', 'dense', 'potentextract', 'insidecloud'],
    labels: { en: 'Dense chase concealment', fr: 'Dissimulation dense en chase' },
    descriptions: {
      en: 'Test maximum inside-cloud opacity as a deliberately narrow, high-variance concealment gamble.',
      fr: 'Tester l’opacité interne maximale comme un pari de dissimulation volontairement court et très variable.'
    },
    conditions: {
      en: 'Potent Extract visibility is currently inconsistent across maps and lighting. This is not a reliable chase recommendation; Mushroom Formula only repairs part of its severe radius loss.',
      fr: 'La visibilité avec Extrait puissant varie actuellement selon les cartes et l’éclairage. Ce n’est pas une recommandation fiable en chase ; Formule fongique ne compense qu’une partie de sa forte perte de rayon.'
    }
  },
  {
    id: 'large_lasting_cloud',
    category: 8,
    target: 'zone',
    phase: 'macro',
    plans: ['linked_teamwork'],
    signals: ['stealth', 'information'],
    keywords: ['occlusion', 'rotation', 'prerun', 'scratchmark', 'disappear'],
    labels: { en: 'Lasting pre-run screen', fr: 'Écran durable de pré-run' },
    descriptions: {
      en: 'Pre-deploy the largest and longest outside-in visual wall, then hide the rotation after line of sight is broken.',
      fr: 'Pré-déployer le mur visuel extérieur le plus grand et le plus durable, puis masquer la rotation après avoir cassé la ligne de vue.'
    },
    conditions: {
      en: 'The Killer must still be outside the cloud; shared inside-cloud concealment is unreliable after 9.5.0.',
      fr: 'Le tueur doit encore être hors du nuage ; la dissimulation quand les deux joueurs sont dedans reste peu fiable depuis la 9.5.0.'
    }
  },
  {
    id: 'fast_deploy_cloud',
    automatic: false,
    category: 8,
    target: 'zone',
    phase: 'macro',
    plans: [],
    signals: ['stealth', 'information'],
    keywords: ['prerun', 'lineofsight', 'rotation', 'occlusion', 'reposition', 'disappear', 'approach'],
    labels: { en: 'Urgent pre-run screen', fr: 'Écran urgent de pré-run' },
    descriptions: {
      en: 'Raise a wide outside-in screen before the approaching Killer crosses into the cloud.',
      fr: 'Déployer un large écran visuel avant que le tueur en approche ne pénètre dans le nuage.'
    },
    conditions: {
      en: 'This is situational pre-run utility, not an emergency chase button once the Killer shares the cloud.',
      fr: 'C’est un outil situationnel de pré-run, pas un bouton d’urgence une fois que le tueur partage le nuage.'
    }
  },
  {
    id: 'repeatable_cloud',
    category: 8,
    target: 'resource',
    phase: 'macro',
    plans: ['item_economy'],
    signals: ['item', 'stealth'],
    keywords: ['recharge', 'repeat', 'resource', 'stealth'],
    labels: { en: 'Repeatable cloud', fr: 'Nuage répétable' },
    descriptions: {
      en: 'Reduce downtime and preserve enough duration or range for repeated uses across the trial.',
      fr: 'Réduire le temps mort et garder assez de durée ou de portée pour répéter les usages pendant la partie.'
    },
    conditions: {
      en: 'Recharge reduction is strategic value, not immediate chase power.',
      fr: 'La réduction de recharge est une valeur stratégique, pas une puissance immédiate en chase.'
    }
  }
]

export const SURVIVOR_ITEM_SCENARIO_BASES = {
// Legacy numeric hints are retained as catalogue coverage metadata only.
// Package strength is derived exclusively from EQUIPMENT_STAT_VALUE_MODEL;
// these values must never be added to a zero-Chaos score.
  broken_key: { team_aura_tracking: 70, killer_aura_tracking: 58, chest_endgame_utility: 28, obsession_coordination: 48 },
  dull_key: { team_aura_tracking: 76, killer_aura_tracking: 62, chest_endgame_utility: 84, obsession_coordination: 58 },
  skeleton_key: { team_aura_tracking: 82, killer_aura_tracking: 68, chest_endgame_utility: 92, obsession_coordination: 62 },

  flashlight: { flashlight_save: 62, chase_blind: 62, blind_duration_pressure: 56, flashlight_endurance: 56 },
  sport_flashlight: { flashlight_save: 88, chase_blind: 62, blind_duration_pressure: 60, flashlight_endurance: 72 },
  utility_flashlight: { flashlight_save: 68, chase_blind: 88, blind_duration_pressure: 79, flashlight_endurance: 86 },

  cryptic_map: { route_scouting: 62, team_objective_mapping: 58, totem_hunting: 44, map_killer_scouting: 42 },
  scribbled_map: { route_scouting: 74, team_objective_mapping: 70, totem_hunting: 54, map_killer_scouting: 52 },
  annotated_map: { route_scouting: 86, team_objective_mapping: 82, totem_hunting: 64, map_killer_scouting: 62 },
  bloodsense_map: { route_scouting: 88, team_objective_mapping: 86, totem_hunting: 68, map_killer_scouting: 66 },

  camping_aid_kit: { double_self_heal: 64, self_heal_safety: 72, altruistic_heal_burst: 68, altruistic_heal_economy: 70, healing_skill_checks: 62, exhaustion_reset: 64 },
  first_aid_kit: { double_self_heal: 64, self_heal_safety: 72, altruistic_heal_burst: 73, altruistic_heal_economy: 75, healing_skill_checks: 64, exhaustion_reset: 65 },
  emergency_med_kit: { double_self_heal: 64, self_heal_safety: 72, altruistic_heal_burst: 82, altruistic_heal_economy: 82, healing_skill_checks: 66, exhaustion_reset: 66 },
  ranger_med_kit: { double_self_heal: 64, self_heal_safety: 72, altruistic_heal_burst: 90, altruistic_heal_economy: 88, healing_skill_checks: 68, exhaustion_reset: 67 },

  worn_out_tools: { generator_burst: 52, generator_capacity: 50, brand_new_part_burst: 50, stealth_repair: 50, sabotage_speed: 28, sabotage_denial: 26 },
  toolbox: { generator_burst: 64, generator_capacity: 64, brand_new_part_burst: 60, stealth_repair: 58, sabotage_speed: 45, sabotage_denial: 42 },
  mechanic_s_toolbox: { generator_burst: 70, generator_capacity: 60, brand_new_part_burst: 68, stealth_repair: 64, sabotage_speed: 54, sabotage_denial: 48 },
  commodious_toolbox: { generator_burst: 88, generator_capacity: 94, brand_new_part_burst: 86, stealth_repair: 76, sabotage_speed: 76, sabotage_denial: 70 },
  engineer_s_toolbox: { generator_burst: 82, generator_capacity: 62, brand_new_part_burst: 84, stealth_repair: 68, sabotage_speed: 34, sabotage_denial: 32 },
  alex_s_toolbox: { generator_burst: 38, generator_capacity: 70, brand_new_part_burst: 54, stealth_repair: 54, sabotage_speed: 90, sabotage_denial: 90 },

  apprentice_s_fog_vial: { dense_chase_concealment: 64, large_lasting_cloud: 58, fast_deploy_cloud: 58, repeatable_cloud: 56 },
  artisan_s_fog_vial: { dense_chase_concealment: 74, large_lasting_cloud: 70, fast_deploy_cloud: 72, repeatable_cloud: 68 },
  vigo_s_fog_vial: { dense_chase_concealment: 84, large_lasting_cloud: 82, fast_deploy_cloud: 86, repeatable_cloud: 80 }
}

export const SURVIVOR_ADDON_SCENARIO_EFFECTS = {
// Presence documents supported Add-ons; the numbers are non-scoring legacy hints.
// Exact mechanics in scenarioMetrics are the single source of optimisation truth.
  friendship_charm: { team_aura_tracking: 7, killer_aura_tracking: 9, chest_endgame_utility: 5, obsession_coordination: 5 },
  shrill_whistle: { team_aura_tracking: 5, killer_aura_tracking: 5, chest_endgame_utility: 13, obsession_coordination: 3 },
  braided_bauble: { team_aura_tracking: 11, killer_aura_tracking: 7, chest_endgame_utility: 6, obsession_coordination: 6 },
  unique_wedding_ring: { team_aura_tracking: 4, killer_aura_tracking: 2, chest_endgame_utility: 2, obsession_coordination: 34 },
  blood_amber: { team_aura_tracking: -15, killer_aura_tracking: 38, chest_endgame_utility: -10, obsession_coordination: -8 },

  battery: { flashlight_save: 3, chase_blind: 2, blind_duration_pressure: 1, flashlight_endurance: 10 },
  heavy_duty_battery: { flashlight_save: 5, chase_blind: 3, blind_duration_pressure: 2, flashlight_endurance: 17 },
  long_life_battery: { flashlight_save: 10, chase_blind: 16, blind_duration_pressure: 3, flashlight_endurance: 25 },
  low_amp_filament: { flashlight_save: 8, chase_blind: 6, blind_duration_pressure: 5, flashlight_endurance: 24 },
  leather_grip: { flashlight_save: 4, chase_blind: 2, blind_duration_pressure: 1, flashlight_endurance: 1 },
  rubber_grip: { flashlight_save: 8, chase_blind: 3, blind_duration_pressure: 2, flashlight_endurance: 1 },
  wide_lens: { flashlight_save: 30, chase_blind: 2, blind_duration_pressure: 1, flashlight_endurance: 0 },
  focus_lens: { flashlight_save: -12, chase_blind: 4, blind_duration_pressure: 7, flashlight_endurance: 1 },
  high_end_sapphire_lens: { flashlight_save: -20, chase_blind: 30, blind_duration_pressure: 9, flashlight_endurance: 1 },
  power_bulb: { flashlight_save: 0, chase_blind: 2, blind_duration_pressure: 5, flashlight_endurance: 0 },
  tir_optic: { flashlight_save: 0, chase_blind: 2, blind_duration_pressure: 7, flashlight_endurance: 0 },
  intense_halogen: { flashlight_save: 0, chase_blind: 3, blind_duration_pressure: 10, flashlight_endurance: 0 },
  odd_bulb: { flashlight_save: -4, chase_blind: 2, blind_duration_pressure: 11, flashlight_endurance: -15 },

  battered_tape: { route_scouting: 12, team_objective_mapping: 3, totem_hunting: 14, map_killer_scouting: 2 },
  glowing_ink: { route_scouting: 8, team_objective_mapping: 3, totem_hunting: 9, map_killer_scouting: 0 },
  gnarled_compass: { route_scouting: 6, team_objective_mapping: 20, totem_hunting: 10, map_killer_scouting: 15 },
  sharpened_flint: { route_scouting: 3, team_objective_mapping: 4, totem_hunting: 40, map_killer_scouting: 2 },
  crimson_stamp: { route_scouting: -12, team_objective_mapping: -8, totem_hunting: -11, map_killer_scouting: 39 },

  bandages: { double_self_heal: 13, self_heal_safety: 9, altruistic_heal_burst: 2, altruistic_heal_economy: 11, healing_skill_checks: 1, exhaustion_reset: 2 },
  gauze_roll: { double_self_heal: 17, self_heal_safety: 11, altruistic_heal_burst: 2, altruistic_heal_economy: 14, healing_skill_checks: 1, exhaustion_reset: 2 },
  gel_dressings: { double_self_heal: 27, self_heal_safety: 16, altruistic_heal_burst: 3, altruistic_heal_economy: 21, healing_skill_checks: 1, exhaustion_reset: 3 },
  butterfly_tape: { double_self_heal: 1, self_heal_safety: 5, altruistic_heal_burst: 8, altruistic_heal_economy: 6, healing_skill_checks: 2, exhaustion_reset: 3 },
  medical_scissors: { double_self_heal: 1, self_heal_safety: 9, altruistic_heal_burst: 14, altruistic_heal_economy: 10, healing_skill_checks: 2, exhaustion_reset: 4 },
  abdominal_dressing: { double_self_heal: 1, self_heal_safety: 12, altruistic_heal_burst: 22, altruistic_heal_economy: 14, healing_skill_checks: 3, exhaustion_reset: 5 },
  self_adherent_wrap: { double_self_heal: 13, self_heal_safety: 12, altruistic_heal_burst: 7, altruistic_heal_economy: 15, healing_skill_checks: 2, exhaustion_reset: 4 },
  rubber_gloves: { double_self_heal: 0, self_heal_safety: 1, altruistic_heal_burst: 1, altruistic_heal_economy: 1, healing_skill_checks: 15, exhaustion_reset: 0 },
  sponge: { double_self_heal: 0, self_heal_safety: 1, altruistic_heal_burst: 1, altruistic_heal_economy: 1, healing_skill_checks: 20, exhaustion_reset: 0 },
  needle_thread: { double_self_heal: 0, self_heal_safety: 2, altruistic_heal_burst: 3, altruistic_heal_economy: 2, healing_skill_checks: 23, exhaustion_reset: 0 },
  surgical_suture: { double_self_heal: 0, self_heal_safety: 2, altruistic_heal_burst: 3, altruistic_heal_economy: 2, healing_skill_checks: 29, exhaustion_reset: 0 },
  styptic_agent: { double_self_heal: 10, self_heal_safety: 22, altruistic_heal_burst: 1, altruistic_heal_economy: 4, healing_skill_checks: 0, exhaustion_reset: 2 },
  anti_exhaustion_syringe: { double_self_heal: -20, self_heal_safety: -12, altruistic_heal_burst: -10, altruistic_heal_economy: -18, healing_skill_checks: -8, exhaustion_reset: 42 },

  clean_rag: { generator_burst: 7, generator_capacity: 2, brand_new_part_burst: 7, stealth_repair: 5, sabotage_speed: 0, sabotage_denial: 0 },
  socket_swivels: { generator_burst: 11, generator_capacity: 3, brand_new_part_burst: 10, stealth_repair: 6, sabotage_speed: 0, sabotage_denial: 0 },
  scraps: { generator_burst: 11, generator_capacity: 18, brand_new_part_burst: 8, stealth_repair: 6, sabotage_speed: 12, sabotage_denial: 10 },
  wire_spool: { generator_burst: 16, generator_capacity: 25, brand_new_part_burst: 13, stealth_repair: 9, sabotage_speed: 18, sabotage_denial: 30 },
  brand_new_part: { generator_burst: 20, generator_capacity: 7, brand_new_part_burst: 36, stealth_repair: 0, sabotage_speed: -8, sabotage_denial: -8 },
  instructions: { generator_burst: -5, generator_capacity: 3, brand_new_part_burst: -8, stealth_repair: 14, sabotage_speed: 0, sabotage_denial: 0 },
  spring_clamp: { generator_burst: 2, generator_capacity: 4, brand_new_part_burst: 0, stealth_repair: 35, sabotage_speed: 0, sabotage_denial: 0 },
  cutting_wire: { generator_burst: 0, generator_capacity: 1, brand_new_part_burst: 0, stealth_repair: 0, sabotage_speed: 8, sabotage_denial: 6 },
  hacksaw: { generator_burst: 0, generator_capacity: 1, brand_new_part_burst: 0, stealth_repair: 0, sabotage_speed: 12, sabotage_denial: 5 },
  grip_wrench: { generator_burst: 0, generator_capacity: 1, brand_new_part_burst: 0, stealth_repair: 0, sabotage_speed: 6, sabotage_denial: 29 },
  protective_gloves: { generator_burst: 0, generator_capacity: 1, brand_new_part_burst: 0, stealth_repair: 0, sabotage_speed: 0, sabotage_denial: 1 },

  mushroom_formula: { dense_chase_concealment: 20, large_lasting_cloud: 23, fast_deploy_cloud: 18, repeatable_cloud: 16 },
  oily_sap: { dense_chase_concealment: 7, large_lasting_cloud: 20, fast_deploy_cloud: 4, repeatable_cloud: 9 },
  potent_extract: { dense_chase_concealment: 34, large_lasting_cloud: -20, fast_deploy_cloud: -18, repeatable_cloud: -20 },
  reactive_compound: { dense_chase_concealment: 6, large_lasting_cloud: 5, fast_deploy_cloud: 28, repeatable_cloud: 5 },
  volcanic_stone: { dense_chase_concealment: 2, large_lasting_cloud: 4, fast_deploy_cloud: 3, repeatable_cloud: 26 }
}

export const SURVIVOR_EQUIPMENT_PAIR_OVERRIDES = [
// Pair entries provide documentation/confidence only. Their bonus is never scored;
// perk-specific package strategies are evaluated explicitly by the strategy engine.
  ['killer_aura_tracking', ['blood_amber', 'friendship_charm'], 13],
  ['killer_aura_tracking', ['blood_amber', 'braided_bauble'], 10],
  ['obsession_coordination', ['unique_wedding_ring', 'friendship_charm'], 11],
  ['chest_endgame_utility', ['shrill_whistle', 'friendship_charm'], 9],

  ['flashlight_save', ['wide_lens', 'long_life_battery'], 16],
  ['flashlight_save', ['wide_lens', 'rubber_grip'], 14],
  ['flashlight_save', ['wide_lens', 'low_amp_filament'], 12],
  ['chase_blind', ['high_end_sapphire_lens', 'long_life_battery'], 18],
  ['chase_blind', ['high_end_sapphire_lens', 'heavy_duty_battery'], 14],
  ['chase_blind', ['high_end_sapphire_lens', 'intense_halogen'], 12],
  ['blind_duration_pressure', ['odd_bulb', 'intense_halogen'], 5],
  ['blind_duration_pressure', ['odd_bulb', 'high_end_sapphire_lens'], 4],
  ['flashlight_endurance', ['long_life_battery', 'low_amp_filament'], 17],
  ['flashlight_endurance', ['long_life_battery', 'heavy_duty_battery'], 12],

  ['totem_hunting', ['sharpened_flint', 'battered_tape'], 13],
  ['totem_hunting', ['sharpened_flint', 'glowing_ink'], 10],
  ['map_killer_scouting', ['crimson_stamp', 'battered_tape'], 5],
  ['map_killer_scouting', ['crimson_stamp', 'gnarled_compass'], 15],
  ['team_objective_mapping', ['battered_tape', 'gnarled_compass'], 11],
  ['team_objective_mapping', ['gnarled_compass', 'glowing_ink'], 9],

  ['double_self_heal', ['gel_dressings', 'gauze_roll'], 18],
  ['double_self_heal', ['gel_dressings', 'bandages'], 16],
  ['double_self_heal', ['gel_dressings', 'self_adherent_wrap'], 16],
  ['self_heal_safety', ['gel_dressings', 'styptic_agent'], 11],
  ['altruistic_heal_burst', ['abdominal_dressing', 'medical_scissors'], 15],
  ['altruistic_heal_burst', ['abdominal_dressing', 'butterfly_tape'], 11],
  ['altruistic_heal_economy', ['self_adherent_wrap', 'gauze_roll'], 10],
  ['healing_skill_checks', ['needle_thread', 'surgical_suture'], 16],
  ['healing_skill_checks', ['surgical_suture', 'sponge'], 12],
  ['exhaustion_reset', ['anti_exhaustion_syringe', 'abdominal_dressing'], 12],
  ['exhaustion_reset', ['anti_exhaustion_syringe', 'self_adherent_wrap'], 10],

  ['generator_burst', ['socket_swivels', 'clean_rag'], 5],
  ['generator_burst', ['socket_swivels', 'wire_spool'], 14],
  ['generator_burst', ['wire_spool', 'scraps'], 8],
  ['generator_capacity', ['wire_spool', 'scraps'], 15],
  ['brand_new_part_burst', ['brand_new_part', 'socket_swivels'], 15],
  ['brand_new_part_burst', ['brand_new_part', 'wire_spool'], 11],
  ['stealth_repair', ['spring_clamp', 'wire_spool'], 10],
  ['stealth_repair', ['spring_clamp', 'socket_swivels'], 9],
  ['sabotage_speed', ['hacksaw', 'cutting_wire'], 2],
  ['sabotage_speed', ['hacksaw', 'wire_spool'], 14],
  ['sabotage_denial', ['grip_wrench', 'wire_spool'], 20],
  ['sabotage_denial', ['grip_wrench', 'hacksaw'], 10],
  ['sabotage_denial', ['grip_wrench', 'cutting_wire'], 8],
  ['sabotage_denial', ['grip_wrench', 'protective_gloves'], 2],

  ['dense_chase_concealment', ['potent_extract', 'mushroom_formula'], 14],
  ['dense_chase_concealment', ['potent_extract', 'reactive_compound'], 4],
  ['large_lasting_cloud', ['mushroom_formula', 'oily_sap'], 15],
  ['fast_deploy_cloud', ['reactive_compound', 'mushroom_formula'], 15],
  ['repeatable_cloud', ['volcanic_stone', 'mushroom_formula'], 14],
  ['repeatable_cloud', ['volcanic_stone', 'oily_sap'], 8]
].map(entry => ({
  scenarioId: entry[0],
  addonIds: entry[1].slice().sort(),
  bonus: entry[2],
  confidence: 'curated'
}))

export const SURVIVOR_EQUIPMENT_RISKS = {
  blood_amber: ['mutual_aura', 'reduced_duration', 'reduced_charges'],
  wide_lens: ['reduced_range'],
  focus_lens: ['reduced_width'],
  high_end_sapphire_lens: ['reduced_width'],
  odd_bulb: ['faster_depletion'],
  crimson_stamp: ['reduced_duration', 'reduced_charges'],
  anti_exhaustion_syringe: ['consumes_item', 'requires_exhaustion'],
  brand_new_part: ['consumed_on_use', 'difficult_skill_check'],
  instructions: ['removes_regular_skill_checks'],
  potent_extract: ['reduced_duration', 'reduced_area']
}

export const SURVIVOR_EQUIPMENT_ITEM_STATS = {
  broken_key: { charges: 6, auraRange: 48, auraDuration: 8 },
  dull_key: { charges: 5, auraRange: 56, auraDuration: 9, chest: true, hatch: true },
  skeleton_key: { charges: 6, auraRange: 64, auraDuration: 10, chest: true, hatch: true },
  flashlight: { batterySeconds: 8, accuracy: 0, blindDurationBonus: 0 },
  sport_flashlight: { batterySeconds: 8, accuracy: 20, depletionReduction: 11, blindDurationBonus: 0 },
  utility_flashlight: { batterySeconds: 12, accuracy: -20, blindDurationBonus: 15 },
  cryptic_map: { charges: 4, routeRange: 24, routeDuration: 10 },
  scribbled_map: { charges: 5, routeRange: 32, routeDuration: 12 },
  annotated_map: { charges: 6, routeRange: 40, routeDuration: 14 },
  bloodsense_map: { charges: 8, routeRange: 48, routeDuration: 14 },
  camping_aid_kit: { charges: 24, altruisticSpeed: 35, selfHealSpeedPenalty: 33, selfHealEfficiencyPenalty: 33 },
  first_aid_kit: { charges: 24, altruisticSpeed: 40, selfHealSpeedPenalty: 33, selfHealEfficiencyPenalty: 33 },
  emergency_med_kit: { charges: 24, altruisticSpeed: 45, selfHealSpeedPenalty: 33, selfHealEfficiencyPenalty: 33 },
  ranger_med_kit: { charges: 24, altruisticSpeed: 50, selfHealSpeedPenalty: 33, selfHealEfficiencyPenalty: 33 },
  worn_out_tools: { charges: 16, repairSpeed: 50, sabotageSpeed: 0 },
  toolbox: { charges: 20, repairSpeed: 50, sabotageSpeed: 15 },
  mechanic_s_toolbox: { charges: 16, repairSpeed: 75, sabotageSpeed: 25 },
  commodious_toolbox: { charges: 32, repairSpeed: 50, sabotageSpeed: 50 },
  engineer_s_toolbox: { charges: 16, repairSpeed: 100, sabotageSpeed: 10 },
  alex_s_toolbox: { charges: 18, repairSpeed: 10, sabotageSpeed: 100 },
  apprentice_s_fog_vial: { charges: 4, radius: 8, expansion: 2, duration: 8, recharge: 70 },
  artisan_s_fog_vial: { charges: 4, radius: 8, expansion: 1.5, duration: 10, recharge: 65 },
  vigo_s_fog_vial: { charges: 4, radius: 8, expansion: 1.2, duration: 12, recharge: 60 }
}
